import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  IdentityA11yAnnouncer,
  IdentityButton,
  IdentityFooterInline,
  IdentityHero,
  IdentityLayout,
  IdentityLoading,
  IDENTITY_BUTTON_LABELS,
  IdentityError as IdentityErrorView,
} from "@speedycore/identity-ui";
import {
  identityGetOAuthClient,
  identityOAuthApprove,
  identitySessionHandoff,
  LoginSuccess,
  OAuthClientInfo,
  sendIdentityTelemetry,
} from "../lib/identityApi";
import { useSilentRefresh } from "../hooks/useSilentRefresh";

function parseScopeParam(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function OAuthApprovePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const clientId = useMemo(() => params.get("client_id") || "", [params]);
  const redirectUri = useMemo(() => params.get("redirect_uri") || "", [params]);
  const state = useMemo(() => params.get("state") || undefined, [params]);
  const requestedScopes = useMemo(() => parseScopeParam(params.get("scope")), [params]);

  const [auth, setAuth] = useState<LoginSuccess | null>(null);
  const [loadBusy, setLoadBusy] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [client, setClient] = useState<OAuthClientInfo | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [a11y, setA11y] = useState("");
  const [a11yType, setA11yType] = useState<"status" | "error">("status");

  useEffect(() => {
    let cancelled = false;
    setLoadBusy(true);
    void identitySessionHandoff("identity_web")
      .then((tokens) => {
        if (cancelled) return;
        if (!tokens) {
          setLoadError("Please sign in to approve this app.");
          const qs = new URLSearchParams();
          if (clientId) qs.set("client_id", clientId);
          if (redirectUri) qs.set("redirect_uri", redirectUri);
          if (state) qs.set("state", state);
          if (requestedScopes.length) qs.set("scope", requestedScopes.join(" "));
          window.setTimeout(() => {
            const q = qs.toString();
            navigate(`/login${q ? `?${q}` : ""}`);
          }, 1500);
          return;
        }
        setAuth(tokens);
        if (!clientId) {
          setLoadError("Missing client_id.");
          return;
        }
        return identityGetOAuthClient(clientId).then((info) => {
          if (cancelled) return;
          setClient(info);
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Could not load app details.");
          void sendIdentityTelemetry("identity.sso_callback_error", {
            error_code: "oauth_load_failed",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, navigate, redirectUri, requestedScopes, state]);

  useSilentRefresh({
    accessToken: auth?.access_token,
    refreshToken: auth?.refresh_token ?? null,
    onTokens: (next) => setAuth((prev) => (prev ? { ...prev, ...next } : next)),
    onExpired: () => {
      void sendIdentityTelemetry("identity.token_expired");
      navigate("/login?expired=1");
    },
    enabled: Boolean(auth),
  });

  const effectiveScopes = useMemo(() => {
    if (requestedScopes.length > 0) return requestedScopes;
    return client?.scopes ?? [];
  }, [requestedScopes, client]);

  async function handleApproveOrDeny(denied: boolean) {
    if (!auth || !client || !redirectUri) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const result = await identityOAuthApprove({
        accessToken: auth.access_token,
        clientId,
        redirectUri,
        state,
        scopes: effectiveScopes,
        denied,
      });
      setA11y(denied ? "Access denied. Redirecting…" : "App approved. Redirecting…");
      window.setTimeout(() => {
        window.location.assign(result.redirect_to);
      }, 400);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not process approval.";
      setActionError(msg);
      setA11y(msg);
      setA11yType("error");
      setActionBusy(false);
      void sendIdentityTelemetry("identity.sso_callback_error", {
        error_code: denied ? "oauth_deny_failed" : "oauth_approve_failed",
      });
    }
  }

  const loading = loadBusy || (!loadError && (!auth || !client));

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type={a11yType} />
      <IdentityLayout
        hero={
          <IdentityHero
            title={client ? `Approve ${client.name}` : "Approve access"}
            subtitle="This app is requesting access to your SpeedyCore account."
          />
        }
        form={
          loading ? (
            <IdentityLoading message="Loading app details…" />
          ) : loadError ? (
            <>
              <IdentityErrorView message={loadError} />
              <div className="sc-identity-links" style={{ marginTop: "1rem" }}>
                <Link to="/auth/login">Back to sign in</Link>
              </div>
              <IdentityFooterInline />
            </>
          ) : auth && client ? (
            <div className="sc-identity-oauth" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <section className="sc-identity-card">
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  <div>
                    <div className="sc-identity-field-label" style={{ fontWeight: 600 }}>App name</div>
                    <div>{client.name}</div>
                  </div>
                  <div>
                    <div className="sc-identity-field-label" style={{ fontWeight: 600 }}>App domain</div>
                    <div style={{ wordBreak: "break-all" }}>
                      <a
                        href={`https://${client.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--sc-identity-primary)" }}
                      >
                        {client.domain}
                      </a>
                    </div>
                  </div>
                  <div>
                    <div className="sc-identity-field-label" style={{ fontWeight: 600, marginBottom: "0.35rem" }}>
                      Requested permissions
                    </div>
                    {effectiveScopes.length === 0 ? (
                      <p className="sc-identity-field-hint" style={{ color: "#5a607a" }}>
                        Basic account access.
                      </p>
                    ) : (
                      <ul className="sc-identity-oauth__scopes" style={{ paddingLeft: "1.25rem", margin: 0 }}>
                        {effectiveScopes.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <p className="sc-identity-oauth__revoke" style={{ textAlign: "center", fontSize: "0.82rem", color: "#5a607a" }}>
                    You can revoke access anytime in <Link to="/auth/settings" style={{ color: "var(--sc-identity-primary)" }}>SpeedyCore Identity Settings</Link>.
                  </p>
                </div>
              </section>
              {actionError ? <IdentityErrorView message={actionError} /> : null}
              <div className="sc-identity-oauth__actions" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                <IdentityButton
                  label={IDENTITY_BUTTON_LABELS.deny}
                  variant="secondary"
                  loading={actionBusy}
                  onClick={() => void handleApproveOrDeny(true)}
                />
                <IdentityButton
                  label={IDENTITY_BUTTON_LABELS.approve}
                  variant="primary"
                  loading={actionBusy}
                  onClick={() => void handleApproveOrDeny(false)}
                />
              </div>
              <div className="sc-identity-links">
                <Link to="/auth/settings">Open Identity Settings</Link>
                <Link to="/auth/login?logged_out=1">Sign out instead</Link>
              </div>
              <IdentityFooterInline />
            </div>
          ) : null
        }
      />
    </>
  );
}
